<template>
  <div class="balanca-page">
    <b-card class="mb-5 border-0 shadow-sm onboarding-card">
      <div class="d-flex flex-wrap justify-content-between align-items-start">
        <div class="onboarding-copy">
          <h3 class="font-weight-bolder mb-2">Balança deste computador</h3>
          <p class="text-muted mb-3">
            Instale o aplicativo uma vez. O ambiente deste PDV, o adaptador e a
            porta da balança serão detectados automaticamente.
          </p>
          <div class="d-flex flex-wrap">
            <b-button
              variant="primary"
              class="mr-2 mb-2"
              :disabled="!downloads.windows"
              @click="baixarAgente(downloads.windows)"
            >
              <i class="flaticon-download mr-1"></i> Baixar Balança para Windows
              (.exe)
            </b-button>
            <b-button
              variant="outline-primary"
              class="mr-2 mb-2"
              :disabled="!downloads.macos"
              @click="baixarAgente(downloads.macos)"
            >
              <i class="flaticon-download mr-1"></i> Baixar Balança para macOS
              (.dmg)
            </b-button>
            <b-button
              variant="success"
              class="mb-2"
              :disabled="pareando"
              @click="conectarEsteComputador"
            >
              <i class="flaticon2-link mr-1"></i>
              {{ pareando ? "Conectando..." : "Conectar este computador" }}
            </b-button>
          </div>
          <small
            v-if="!downloads.windows || !downloads.macos"
            class="text-warning d-block mt-1"
          >
            Os instaladores ainda não foram publicados neste ambiente.
          </small>
          <small v-else class="text-muted d-block mt-1">
            Versão {{ downloads.version }} · Depois de instalar, clique em
            “Conectar este computador”.
          </small>
        </div>

        <div class="status-local" :class="statusLocal.classe">
          <strong>{{ statusLocal.titulo }}</strong>
          <span>{{ statusLocal.mensagem }}</span>
          <b-button
            size="sm"
            variant="link"
            class="p-0 mt-1"
            @click="verificarAgenteLocal"
            >Verificar novamente</b-button
          >
        </div>
      </div>
    </b-card>

    <FilterFields
      :fields="[
        { type: 'text', label: 'Nome', field: 'nome' },
        {
          type: 'select',
          label: 'Status',
          field: 'status',
          options: ['Todos', 'online', 'offline'],
        },
      ]"
    />

    <TableApp
      titulo="Balanças"
      :acaoBtn="null"
      api-url="/balanca/dispositivos/lista"
      :fields="fields"
      classFilterColumn="nome"
      ref="balanca"
      :perPage="10"
    >
      <template slot="customRow" slot-scope="props">
        <span v-if="props.field === 'nome'">
          <span class="text-dark-75 font-weight-bold d-block">{{
            props.rowData.nome
          }}</span>
          <span class="text-muted font-size-sm">{{
            nomeModelo(props.rowData.modelo)
          }}</span>
        </span>

        <span v-if="props.field === 'porta_serial'">
          <span class="text-dark-75 font-weight-bold d-block">{{
            props.rowData.porta_serial || "Aguardando o aplicativo"
          }}</span>
          <span class="text-muted font-size-sm">{{
            props.rowData.sistema_operacional || "—"
          }}</span>
        </span>

        <span v-if="props.field === 'status'">
          <span
            class="badge"
            :class="props.rowData.online ? 'badge-success' : 'badge-secondary'"
          >
            {{ props.rowData.online ? "Online" : "Offline" }}
          </span>
        </span>

        <span v-if="props.field === 'actions'">
          <div class="d-flex">
            <button
              class="btn btn-sm btn-icon btn-light-success mr-2"
              v-b-tooltip="'Conectar neste computador'"
              @click="parearDispositivo(props.rowData)"
            >
              <i class="flaticon2-link"></i>
            </button>
            <button
              class="btn btn-sm btn-icon btn-light-primary mr-2"
              v-b-tooltip="'Editar'"
              @click="abrirModalEditar(props.rowData)"
            >
              <i class="flaticon2-pen"></i>
            </button>
            <button
              class="btn btn-sm btn-icon btn-light-danger"
              v-b-tooltip="'Excluir'"
              @click="excluir(props.rowData)"
            >
              <i class="flaticon2-trash"></i>
            </button>
          </div>
        </span>
      </template>
    </TableApp>

    <b-modal
      ref="modalDispositivo"
      title="Configuração da balança"
      centered
      no-close-on-backdrop
      @ok="salvar"
      :ok-title="salvando ? 'Salvando...' : 'Salvar'"
      :ok-disabled="salvando"
    >
      <b-form @submit.prevent="salvar">
        <b-form-group label="Nome do caixa"
          ><b-form-input v-model="form.nome"
        /></b-form-group>
        <b-form-group label="Modelo da balança"
          ><b-form-select
            v-model="form.modelo"
            :options="opcoesModelos"
            @change="onModeloChange"
        /></b-form-group>
        <b-row>
          <b-col
            ><b-form-group label="Velocidade"
              ><b-form-select
                v-model="form.baud_rate"
                :options="[2400, 4800, 9600, 19200]" /></b-form-group
          ></b-col>
          <b-col
            ><b-form-group label="Casas decimais"
              ><b-form-input
                v-model.number="form.casas_decimais"
                type="number"
                min="0"
                max="5" /></b-form-group
          ></b-col>
        </b-row>
        <p class="text-muted font-size-sm mb-0">
          A porta USB é detectada e mantida pelo aplicativo instalado neste
          computador.
        </p>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import swal from "sweetalert";
import TableApp from "@/components/Table/Table2";
import FilterFields from "@/components/FilterFields";
import balancaService from "@/core/services/balanca.service";
import balancaTerminalService from "@/core/services/balanca-terminal.service";

export default {
  name: "balanca-index",
  components: { TableApp, FilterFields },
  data() {
    return {
      fields: [
        {
          label: "Dispositivo",
          field: "nome",
          sortable: true,
          slot: true,
          width: "30%",
        },
        {
          label: "Computador",
          field: "porta_serial",
          sortable: false,
          slot: true,
          width: "30%",
        },
        {
          label: "Status",
          field: "status",
          sortable: true,
          slot: true,
          width: "15%",
        },
        {
          label: "Ações",
          field: "actions",
          sortable: false,
          slot: true,
          width: "25%",
        },
      ],
      modelos: [],
      downloads: { windows: null, macos: null, version: null },
      diagnosticoLocal: null,
      pareando: false,
      salvando: false,
      form: {},
    };
  },
  computed: {
    opcoesModelos() {
      return this.modelos.map((modelo) => ({
        value: modelo.codigo,
        text: `${modelo.nome} (${modelo.fabricante})`,
      }));
    },
    statusLocal() {
      if (this.diagnosticoLocal?.conectado) {
        return {
          classe: "is-ready",
          titulo: "Pronta para usar",
          mensagem: this.diagnosticoLocal.porta || "Balança conectada",
        };
      }
      if (this.diagnosticoLocal?.pareado) {
        return {
          classe: "is-warning",
          titulo: "Aplicativo encontrado",
          mensagem:
            this.diagnosticoLocal.ultimoErro || "Conecte ou ligue a balança",
        };
      }
      return {
        classe: "is-offline",
        titulo: "Ainda não conectada",
        mensagem: "Instale o aplicativo e conecte este computador",
      };
    },
  },
  async created() {
    balancaService.configurar({
      host: "127.0.0.1",
      porta: 51234,
      accessToken: balancaTerminalService.localAccessToken(),
    });
    await Promise.all([this.carregarModelos(), this.carregarDownloads()]);
    await this.verificarAgenteLocal();
  },
  methods: {
    async carregarModelos() {
      try {
        const { data } = await this.$http.post("/balanca/modelos");
        this.modelos = data || [];
      } catch (_) {
        this.modelos = [];
      }
    },
    async carregarDownloads() {
      try {
        const { data } = await this.$http.post("/balanca/downloads");
        this.downloads = data || this.downloads;
      } catch (_) {
        /* O cadastro continua disponível mesmo sem artefatos publicados. */
      }
    },
    baixarAgente(url) {
      if (url) window.open(url, "_blank", "noopener");
    },
    nomeModelo(codigo) {
      return (
        this.modelos.find((modelo) => modelo.codigo === codigo)?.nome ||
        codigo ||
        "—"
      );
    },
    onModeloChange(codigo) {
      const modelo = this.modelos.find((item) => item.codigo === codigo);
      if (modelo) {
        this.form.baud_rate = modelo.baud_rate_default;
        this.form.casas_decimais = modelo.casas_decimais_default;
      }
    },
    async dispositivoDesteComputador() {
      const { data } = await this.$http.post("/balanca/dispositivos/lista", {
        terminal_web_uuid: balancaTerminalService.terminalUuid(),
        ativo: true,
        per_page: 1,
      });
      return data?.data?.[0] || null;
    },
    async conectarEsteComputador() {
      this.pareando = true;
      try {
        let dispositivo = await this.dispositivoDesteComputador();
        let resposta;
        if (dispositivo) {
          resposta = await this.$http.post("/balanca/dispositivos/parear", {
            id: dispositivo.id,
          });
        } else {
          resposta = await this.$http.post("/balanca/dispositivos/create", {
            nome: "PDV Web deste computador",
            modelo: "toledo_prix_3_fit",
            terminal_web_uuid: balancaTerminalService.terminalUuid(),
          });
          dispositivo = resposta.data;
        }
        const codigo =
          resposta.data?.codigo_pareamento ||
          resposta.data?.data?.codigo_pareamento;
        if (!codigo)
          throw new Error("O servidor não gerou o código de pareamento.");
        this.abrirAgente(codigo);
        await this.aguardarAgente();
        this.$refs.balanca?.refresh?.();
      } catch (error) {
        swal({
          text: error?.message || "Não foi possível conectar este computador.",
          icon: "error",
        });
      } finally {
        this.pareando = false;
      }
    },
    abrirAgente(codigo) {
      balancaTerminalService.abrirAgente(codigo);
    },
    async aguardarAgente() {
      for (let tentativa = 0; tentativa < 15; tentativa += 1) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
        if (await this.verificarAgenteLocal()) return true;
      }
      swal({
        text: "O aplicativo não respondeu. Confirme que ele foi instalado e está aberto.",
        icon: "warning",
      });
      return false;
    },
    async verificarAgenteLocal() {
      try {
        this.diagnosticoLocal = await balancaService.diagnostico();
        return Boolean(this.diagnosticoLocal?.pareado);
      } catch (_) {
        this.diagnosticoLocal = null;
        return false;
      }
    },
    async parearDispositivo(row) {
      this.pareando = true;
      try {
        await this.$http.post("/balanca/dispositivos/update", {
          id: row.id,
          terminal_web_uuid: balancaTerminalService.terminalUuid(),
        });
        const { data } = await this.$http.post("/balanca/dispositivos/parear", {
          id: row.id,
        });
        this.abrirAgente(
          data?.codigo_pareamento || data?.data?.codigo_pareamento
        );
        await this.aguardarAgente();
        this.$refs.balanca?.refresh?.();
      } catch (error) {
        swal({
          text: error?.message || "Não foi possível conectar esta balança.",
          icon: "error",
        });
      } finally {
        this.pareando = false;
      }
    },
    abrirModalEditar(row) {
      this.form = {
        id: row.id,
        nome: row.nome,
        modelo: row.modelo,
        baud_rate: row.baud_rate,
        casas_decimais: row.casas_decimais,
      };
      this.$refs.modalDispositivo.show();
    },
    async salvar(event) {
      if (event?.preventDefault) event.preventDefault();
      if (!this.form.nome) return;
      try {
        this.salvando = true;
        await this.$http.post("/balanca/dispositivos/update", this.form);
        this.$refs.balanca?.refresh?.();
        this.$refs.modalDispositivo.hide();
      } finally {
        this.salvando = false;
      }
    },
    excluir(row) {
      swal({
        title: "Excluir balança?",
        text: `O caixa "${row.nome}" deixará de receber o peso.`,
        icon: "warning",
        buttons: { cancel: "Cancelar", confirm: "Excluir" },
        dangerMode: true,
      }).then(async (confirma) => {
        if (!confirma) return;
        await this.$http.post("/balanca/dispositivos/delete", { id: row.id });
        this.$refs.balanca?.refresh?.();
      });
    },
  },
};
</script>

<style scoped>
.onboarding-card {
  border-left: 4px solid #3699ff !important;
}
.onboarding-copy {
  max-width: 720px;
}
.status-local {
  min-width: 230px;
  border-radius: 10px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
}
.status-local.is-ready {
  background: #e8fff3;
  color: #1b7a4b;
}
.status-local.is-warning {
  background: #fff8dd;
  color: #8a6813;
}
.status-local.is-offline {
  background: #f3f6f9;
  color: #5e6278;
}
.status-local span {
  font-size: 12px;
  margin-top: 4px;
}
</style>
